import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { useAuth } from '../auth/AuthContext';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { StatusBadge } from '../components/StatusBadge';
import type { AuditEvent } from '../domain/inventory';
import { statusFamilies } from '../domain/status';
import {
  diffAuditPayloads,
  extractVersion,
  formatItemAuditValue,
} from '../features/item-detail/audit-diff';
import {
  computeObservedFamilies,
  lifecycleFamilyOrder,
  statusFromPayload,
} from '../features/item-detail/lifecycle';
import { DeleteItemsDialog } from '../features/item-delete/DeleteItemsDialog';
import { EditItemDrawer } from '../features/item-edit/EditItemDrawer';
import { TransferDrawer } from '../features/item-transfer/TransferDrawer';
import { toMap } from '../lib/collections';
import { humanizeIdentifier } from '../lib/formatting';
import { siteLabel } from '../lib/site-label';

const HISTORY_PAGE_SIZE = 20;

const FAMILY_LABELS: Record<string, string> = {
  procurement: 'Procurement',
  logistics: 'Logistics',
  deployed: 'Deployed',
  service: 'Service',
  return: 'Return',
  storage: 'Storage',
  'end-of-life': 'Scrap',
};

function formatDateTime(value: string | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not recorded'
    : date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function formatDate(value: string | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toISOString().slice(0, 10);
}

export function ItemDetailPage() {
  const { itemId = '' } = useParams();
  const navigate = useNavigate();
  const { canMutate } = useAuth();
  const [historyPages, setHistoryPages] = useState<AuditEvent[][]>([]);
  const [historyToken, setHistoryToken] = useState<string | undefined>(undefined);
  const [editMode, setEditMode] = useState<'edit' | 'clear-attention' | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const item = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => inventoryApi.getItem(itemId),
    enabled: Boolean(itemId),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const history = useQuery({
    queryKey: ['item-history', itemId, historyToken],
    queryFn: () =>
      inventoryApi.getItemHistory(itemId, { pageSize: HISTORY_PAGE_SIZE, pageToken: historyToken }),
    enabled: Boolean(itemId),
  });

  const sitesById = useMemo(() => toMap(sites.data), [sites.data]);
  const siteCodeById = useMemo(
    () => new Map((sites.data ?? []).map((site) => [site.id, site.name])),
    [sites.data],
  );
  const customersById = useMemo(() => toMap(customers.data), [customers.data]);
  const partsById = useMemo(() => toMap(parts.data), [parts.data]);

  const allHistoryEvents = useMemo(
    () => [...historyPages.flat(), ...(history.data?.data ?? [])],
    [historyPages, history.data],
  );

  function loadEarlierEvents(): void {
    if (history.data) {
      setHistoryPages((pages) => [...pages, history.data.data]);
      setHistoryToken(history.data.nextPageToken);
    }
  }

  if (item.isLoading) return <PageLoadingState label="Loading item…" />;
  if (item.isError || !item.data) {
    return (
      <ErrorState
        title="Item could not be loaded"
        rpcName="GetItem"
        onRetry={() => void item.refetch()}
      />
    );
  }

  const data = item.data;
  const part = partsById.get(data.partId);
  const assignedSite = sitesById.get(data.siteId);
  const locationSite = sitesById.get(data.locationSiteId ?? data.siteId);
  const customer = customersById.get(assignedSite?.customerId ?? '');
  const family = statusFamilies[data.status ?? 'STATUS_UNSPECIFIED'];
  const lifecycleSegments = computeObservedFamilies(allHistoryEvents, data.status);
  const hasRecordedTransfer = Boolean(
    data.lastTransfer?.reason && data.lastTransfer.reason !== 'REASON_UNSPECIFIED',
  );

  return (
    <>
      <PageHeader
        backLink={<Link to="/inventory">Inventory</Link>}
        title={<span className="mono item-detail-title">{data.id}</span>}
        description={
          <>
            {part ? humanizeIdentifier(part.type) : 'Unknown part type'} · {data.partId}
            {part?.build ? ` · ${humanizeIdentifier(part.build)}` : ''}
          </>
        }
        actions={
          <div className="page-actions-group item-detail-actions">
            <span className="version-label">version {data.version}</span>
            <div className="item-detail-action-buttons">
              <button
                type="button"
                onClick={() => setIsTransferring(true)}
                disabled={!canMutate}
                title={canMutate ? 'Transfer this item' : 'Sign in to transfer inventory'}
              >
                Transfer
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => setEditMode('edit')}
                disabled={!canMutate}
                title={canMutate ? 'Edit this item' : 'Sign in to edit inventory'}
              >
                Edit item
              </button>
              <button
                type="button"
                className="button-danger"
                onClick={() => setIsDeleting(true)}
                disabled={!canMutate}
                title={canMutate ? 'Delete this item' : 'Sign in to delete inventory'}
              >
                Delete item
              </button>
            </div>
          </div>
        }
      />

      {data.needsAttention ? (
        <div className="attention-banner">
          <div>
            <strong>Needs attention</strong>
            <p>{data.attentionReason?.trim() || 'No reason recorded'}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditMode('clear-attention')}
            disabled={!canMutate}
            title={canMutate ? 'Edit the attention flag' : 'Sign in to edit inventory'}
          >
            Clear flag…
          </button>
        </div>
      ) : null}

      <div className="item-detail-grid">
        <div className="item-detail-left">
          <section className="panel">
            <h2>Overview</h2>
            <dl className="detail-definition-grid">
              <dt>Status</dt>
              <dd>
                <StatusBadge status={data.status} />
              </dd>
              {data.replacedByItemId ? (
                <>
                  <dt>Replaced by</dt>
                  <dd>
                    <Link
                      to={`/inventory/${encodeURIComponent(data.replacedByItemId)}`}
                      className="mono"
                    >
                      {data.replacedByItemId}
                    </Link>
                    {data.replacedAt ? ` since ${data.replacedAt.slice(0, 10)}` : ''}
                  </dd>
                </>
              ) : null}
              <dt>Lifecycle family</dt>
              <dd>{FAMILY_LABELS[family] ?? family}</dd>
              <dt>Assigned site</dt>
              <dd>{siteLabel(assignedSite, data.siteId)}</dd>
              <dt>Customer</dt>
              <dd>{customer?.displayName ?? customer?.name ?? '—'}</dd>
              <dt>Current location site</dt>
              <dd>{siteLabel(locationSite, data.locationSiteId ?? '—')}</dd>
              <dt>Location detail</dt>
              <dd>{data.location || 'Not recorded'}</dd>
              <dt>Identity</dt>
              <dd>{data.identity || '—'}</dd>
              <dt>Hardware revision</dt>
              <dd>{data.hardwareRevision || 'Not recorded'}</dd>
              <dt>Version</dt>
              <dd className="mono">{data.version}</dd>
              <dt className="detail-span-3">Notes</dt>
              <dd className="detail-span-3">{data.notes || 'No notes recorded.'}</dd>
            </dl>
          </section>

          <section className="panel">
            <h2>Lifecycle</h2>
            <ol className="lifecycle-rail">
              {lifecycleSegments.map((segment) => (
                <li
                  key={segment.family}
                  className={[
                    'lifecycle-segment',
                    segment.observed ? 'lifecycle-segment-observed' : '',
                    segment.current ? 'lifecycle-segment-current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    segment.observed ? { background: `var(--family-${segment.family})` } : undefined
                  }
                  title={FAMILY_LABELS[segment.family]}
                />
              ))}
            </ol>
            <div className="lifecycle-labels">
              {lifecycleFamilyOrder.map((f) => (
                <span key={f}>{FAMILY_LABELS[f]}</span>
              ))}
            </div>
          </section>

          <section className="panel">
            <h2>Dates, transfer and service</h2>
            <dl className="detail-definition-grid">
              <dt>Inspected</dt>
              <dd>
                {formatDate(data.inspectedDate)}
                {data.inspectedBy?.trim() ? (
                  <span className="dimmed-inline"> · {data.inspectedBy.trim()}</span>
                ) : null}
              </dd>
              <dt>Maintained</dt>
              <dd>
                {formatDate(data.maintenanceDate)}
                {data.maintenanceBy?.trim() ? (
                  <span className="dimmed-inline"> · {data.maintenanceBy.trim()}</span>
                ) : null}
              </dd>
              <dt>Shipping date</dt>
              <dd>{formatDate(data.shippingDate)}</dd>
              <dt>Installation date</dt>
              <dd>{formatDate(data.installationDate)}</dd>
              <dt>Decommission date</dt>
              <dd>{formatDate(data.decommissionDate)}</dd>
              <dt>Last transfer</dt>
              <dd>
                {hasRecordedTransfer ? (
                  <>
                    {data.lastTransfer?.reason}
                    {data.lastTransfer?.trackingNumber ? (
                      <span className="dimmed-inline">
                        {' '}
                        · tracking {data.lastTransfer.trackingNumber}
                      </span>
                    ) : null}
                    <span className="dimmed-inline mono">
                      {' '}
                      · {formatDateTime(data.lastTransfer?.transferTime)}
                    </span>
                  </>
                ) : (
                  'No transfer recorded'
                )}
              </dd>
              <dt className="detail-span-3">Maintenance notes</dt>
              <dd className="detail-span-3">
                {data.maintenanceNotes?.trim() || 'No maintenance notes recorded.'}
              </dd>
            </dl>
          </section>
        </div>

        <div className="item-detail-right">
          <section className="panel">
            <h2>Audit history</h2>
            {history.isLoading && historyPages.length === 0 ? (
              <PageLoadingState label="Loading audit history…" className="page-loading-inline" />
            ) : null}
            {history.isError ? <p role="alert">Audit history could not be loaded.</p> : null}
            {allHistoryEvents.length === 0 && !history.isLoading ? (
              <div className="audit-empty-state">
                <strong>No changes recorded</strong>
                <p>Updates to this item will appear here.</p>
              </div>
            ) : null}
            {allHistoryEvents.length > 0 ? (
              <ol className="audit-timeline">
                {allHistoryEvents.map((event) => {
                  const diffs = diffAuditPayloads(event.old, event.new);
                  const oldVersion = extractVersion(event.old);
                  const newVersion = extractVersion(event.new);
                  const resultingStatus =
                    statusFromPayload(event.new) ?? statusFromPayload(event.old);
                  const dotFamily = resultingStatus ? statusFamilies[resultingStatus] : undefined;
                  return (
                    <li key={event.auditId} className="audit-event">
                      <span
                        className="audit-dot"
                        style={dotFamily ? { background: `var(--family-${dotFamily})` } : undefined}
                        aria-hidden="true"
                      />
                      <div className="audit-event-body">
                        <div className="audit-event-header">
                          <span className="mono audit-operation">
                            {event.operationType}
                            {oldVersion !== undefined && newVersion !== undefined
                              ? ` · version ${oldVersion} to ${newVersion}`
                              : ''}
                          </span>
                          <span className="mono audit-meta">
                            {formatDateTime(event.changedAt)} · {event.changedBy ?? 'Unknown actor'}
                          </span>
                        </div>
                        {diffs.length > 0 ? (
                          <dl className="audit-diff-grid">
                            {diffs.map((diff) => (
                              <div className="audit-diff-row" key={diff.field}>
                                <dt>{diff.field}</dt>
                                <dd>
                                  <span className="audit-diff-old">
                                    {formatItemAuditValue(
                                      diff.field,
                                      diff.oldValue,
                                      siteCodeById,
                                    )}
                                  </span>
                                  {' to '}
                                  <span className="audit-diff-new">
                                    {formatItemAuditValue(
                                      diff.field,
                                      diff.newValue,
                                      siteCodeById,
                                    )}
                                  </span>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : null}
            {history.data?.nextPageToken ? (
              <button type="button" onClick={loadEarlierEvents} disabled={history.isFetching}>
                Load earlier events
              </button>
            ) : null}
          </section>
        </div>
      </div>

      {editMode ? (
        <EditItemDrawer
          item={data}
          sites={sites.data ?? []}
          mode={editMode}
          onClose={() => setEditMode(null)}
        />
      ) : null}
      {isTransferring ? (
        <TransferDrawer
          item={data}
          sites={sites.data ?? []}
          onClose={() => setIsTransferring(false)}
        />
      ) : null}
      {isDeleting ? (
        <DeleteItemsDialog
          items={[data]}
          onClose={() => setIsDeleting(false)}
          onDeleted={() => void navigate('/inventory')}
        />
      ) : null}
    </>
  );
}
